import React from "react";
import ProtectedRoute from "routes/ProtectedRoute";
import { protectedRouteLoader } from "routes/loaders/protectedRouteLoader";
import briefcaseRoutes from "./adminRoutes/briefcaseRoutes";
import workspaceRoutes from "./adminRoutes/workspaceRoutes";
import eventsRoutes from "./adminRoutes/eventsRoutes";
import pollsRoutes from "./adminRoutes/pollsRoutes";
import directoryRoutes from "./adminRoutes/directoryRoutes";
import bookingsRoutes from "./adminRoutes/bookingsRoutes";
import inboxRoutes from "./adminRoutes/inboxRoutes";
import reviewRoutes from "./adminRoutes/reviewRoutes";
import tweekEditorRoute from "routes/FlagsRoutes/TweekEditorRoute";
import calendarRoutes from "./adminRoutes/calendarRoutes"
import automationRoutes from "./adminRoutes/automationRoutes"
import { blockOnLive } from "routes/guards/liveGuard";
import { isLive } from "utils/environment";

const adminRoutes = [
  {
    path: "events/details/:eventId/participants/print",
    lazy: async () => {
      const mod = await import("views/Admin/Events/EventParticipantPrint");
      const EventParticipantPrint = mod.default;
      return {
        Component: () => (
          <ProtectedRoute>
            <EventParticipantPrint />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "automation/:id",
    lazy: async () => {
      const mod = await import("views/Admin/Automation/automation-builder");
      const AutomationBuilder = mod.default;
      return {
        Component: () => (
          <ProtectedRoute>
            <AutomationBuilder />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "automation/template/update/:id",
    lazy: async () => {
      const mod = await import("views/Admin/Automation/automation-builder");
      const AutomationBuilder = mod.default;
      return {
        Component: () => (
          <ProtectedRoute>
            <AutomationBuilder isTemplate />
          </ProtectedRoute>
        ),
      };
    },
  },
  {
    path: "",
    loader: protectedRouteLoader,

    // Lazy-load the layout + error boundary
    lazy: async () => {
      const [layoutMod, errorMod] = await Promise.all([
        import("components/UI/AdminLayout"),
        import("views/ErrorPage"),
      ]);

      const AdminLayout = layoutMod.default;
      const ErrorPage = errorMod.default;

      return {
        Component: () => (
          <ProtectedRoute>
            <AdminLayout />
          </ProtectedRoute>
        ),
        ErrorBoundary: ErrorPage,
      };
    },

    children: [
      inboxRoutes,
      {
        path: "presentation-details_",
        lazy: async () => {
          const mod = await import(
            "views/Admin/Presentation/PresentationDetailsNew"
          );
          const Page = mod.default;
          return { Component: Page };
        },
      },

      briefcaseRoutes,
      workspaceRoutes,
      eventsRoutes,
      // Live-only: event details renders the analytics page directly under
      // AdminLayout (no EventsLayout). On non-live, eventsRoutes handles details.
      ...(isLive()
        ? [
            // Canonical live URL for a single event's analytics page.
            {
              path: "presentations/details/:eventId",
              lazy: async () => {
                const mod = await import(
                  "views/Admin/Events/EventAnalyticsPage"
                );
                const EventAnalyticsPage = mod.default;
                return { Component: EventAnalyticsPage };
              },
            },
            // Alias: legacy "/events/details/:eventId" links (calendar, manage-event,
            // reviews, etc.) still resolve to the same analytics page on live.
            {
              path: "events/details/:eventId",
              lazy: async () => {
                const mod = await import(
                  "views/Admin/Events/EventAnalyticsPage"
                );
                const EventAnalyticsPage = mod.default;
                return { Component: EventAnalyticsPage };
              },
            },
            // Live-only alias: the "Presentations" nav points here; renders the
            // same events list (under EventsLayout) as /events.
            {
              path: "presentations",
              lazy: async () => {
                const mod = await import("components/UI/EventsLayout");
                const EventsLayout = mod.default;
                return { Component: EventsLayout };
              },
              children: [
                {
                  index: true,
                  lazy: async () => {
                    const mod = await import("views/Admin/Events");
                    const Events = mod.default;
                    return { Component: Events };
                  },
                },
              ],
            },
          ]
        : []),
      directoryRoutes,
      pollsRoutes,
      bookingsRoutes,
      reviewRoutes,
      tweekEditorRoute,
      calendarRoutes,
      automationRoutes,
      {
        path: "presentation-metric/:presentationId",
        lazy: async () => {
          const mod = await import(
            "views/Admin/Presentation/PresentationMetrics"
          );
          const Page = mod.default;
          return { Component: Page };
        },
      },

      {
        path: "mydocuments",
        lazy: async () => {
          const mod = await import("views/Admin/Documents");
          const Page = mod.default;
          return { Component: Page };
        },
      },
      {
        path: "team",
        lazy: async () => {
          const mod = await import("views/Admin/Team");
          const Page = mod.default;
          return { Component: Page };
        },
      },

      {
        path: "checkout/:id",
        lazy: async () => {
          const mod = await import("views/Admin/Team/Checkout");
          const Page = mod.default;
          return { Component: Page };
        },
      },

      {
        path: "settings",
        lazy: async () => {
          const mod = await import("views/Admin/Settings");
          const Page = mod.default;
          return { Component: Page };
        },
      },
      {
        path: "past-events",

        // Hidden on live: redirect to /overview (see liveGuard).
        lazy: blockOnLive(async () => {
          const mod = await import("components/UI/EventsLayout");
          const EventsLayout = mod.default;
          return { Component: EventsLayout };
        }),

        children: [
          {
            index: true,
            lazy: async () => {
              const mod = await import("views/Admin/Events/past-events");
              const PastEvents = mod.default;
              return { Component: PastEvents };
            },
          },
        ],
      },
      {
        path: "templates",
         lazy: async () => {
          const mod = await import(
            "views/Admin/Settings/NavSetting/feedback-question-settings"
          );
          const Page = mod.FeedbackQuestionSettings;
          return { Component: Page };
        },
      },
    ],
  },
];

export default adminRoutes;
